import React, { useEffect, useMemo, useState } from 'react';
import { X, Send, CheckCircle2, MessageCircle, Plane, Building2, Package as PackageIcon, StampIcon, Camera, Boxes, Car } from 'lucide-react';
import { createEnquiry } from '../lib/api';
import { company } from '../mock';
import { useToast } from '../hooks/use-toast';

const CATEGORIES = [
  { key: 'Flight', label: 'Flight', icon: Plane },
  { key: 'Hotel', label: 'Hotel', icon: Building2 },
  { key: 'Package', label: 'Package', icon: PackageIcon },
  { key: 'Visa', label: 'Visa', icon: StampIcon },
  { key: 'SightSeeing', label: 'SightSeeing', icon: Camera },
  { key: 'Miscellaneous', label: 'Miscellaneous', icon: Boxes },
  { key: 'Transfer', label: 'Transfer', icon: Car },
];

// Field sets per category. `req` marks required. `full` spans the full row.
const FIELD_SETS = {
  Flight: [
    { name: 'source_city', label: 'Type Source City', type: 'text', req: true },
    { name: 'destination_city', label: 'Type Destination City', type: 'text', req: true },
    { name: 'trip_type', label: '--Select Trip Type *--', type: 'select', req: true, full: true, options: ['One Way', 'Round Trip', 'Multi City'] },
    { name: 'departure', label: 'Departure', type: 'date', req: true },
    { name: 'return_date', label: 'Return', type: 'date' },
    { name: 'pax', label: 'No. of Pax', type: 'number', full: true },
  ],
  Hotel: [
    { name: 'destination', label: 'Destination', type: 'text', req: true, full: true },
    { name: 'check_in', label: 'Check In', type: 'date', req: true },
    { name: 'check_out', label: 'Check Out', type: 'date', req: true },
    { name: 'nationality', label: 'Nationality', type: 'text', full: true },
    { name: 'pax', label: 'No. of Pax', type: 'number', full: true },
  ],
  Package: [
    { name: 'travel_date', label: 'Travel Date', type: 'date', req: true, full: true },
    { name: 'destination', label: 'Destination', type: 'text', req: true, full: true },
    { name: 'pax', label: 'No. of Pax', type: 'number', full: true },
  ],
  Visa: [
    { name: 'destination', label: 'Destination Country', type: 'text', req: true, full: true },
    { name: 'nationality', label: 'Nationality', type: 'text', full: true },
    { name: 'travel_date', label: 'Travel Date', type: 'date', req: true, full: true },
    { name: 'pax', label: 'No. of Pax', type: 'number', full: true },
  ],
  SightSeeing: [
    { name: 'destination', label: 'Destination', type: 'text', req: true, full: true },
    { name: 'travel_date', label: 'Travel Date', type: 'date', req: true, full: true },
    { name: 'pax', label: 'No. of Pax', type: 'number', full: true },
  ],
  Miscellaneous: [],
  Transfer: [
    { name: 'source_city', label: 'Type Source City', type: 'text', req: true },
    { name: 'destination_city', label: 'Type Destination City', type: 'text', req: true },
    { name: 'travel_date', label: 'Travel Date', type: 'date', req: true, full: true },
    { name: 'pax', label: 'No. of Pax', type: 'number', full: true },
  ],
};

const SALUTATIONS = ['Mr', 'Mrs', 'Ms', 'Dr'];

const inputCls =
  'w-full rounded-lg border border-slate-300 px-4 py-3 text-[15px] text-[#0B2545] placeholder-slate-400 outline-none focus:border-[#EA5019] focus:ring-2 focus:ring-[#EA5019]/15 transition bg-white';

function IndiaFlag() {
  return (
    <span className="inline-block h-3.5 w-5 rounded-[2px] overflow-hidden border border-slate-200 align-middle" aria-hidden>
      <span className="block h-1/3 bg-[#FF9933]" />
      <span className="block h-1/3 bg-white" />
      <span className="block h-1/3 bg-[#138808]" />
    </span>
  );
}

export default function EnquiryModal({ open, initialCategory = 'Flight', initialForm = {}, onClose }) {
  const { toast } = useToast();
  const [category, setCategory] = useState(initialCategory);
  const [robot, setRobot] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({});

  useEffect(() => {
    if (open) {
      setCategory(initialCategory || 'Flight');
      setForm(initialForm || {});
      setErrors({});
      setRobot(false);
      setSent(false);
      document.body.style.overflow = 'hidden';
    }
    return () => { document.body.style.overflow = ''; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialCategory]);

  const fields = useMemo(() => FIELD_SETS[category] || [], [category]);

  const setField = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.first_name?.trim()) e.first_name = true;
    if (!form.last_name?.trim()) e.last_name = true;
    if (!form.mobile?.trim()) e.mobile = true;
    if (!form.email?.trim()) e.email = true;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = true;
    fields.forEach((fl) => { if (fl.req && !form[fl.name]?.trim?.()) e[fl.name] = true; });
    if (category === 'Miscellaneous' && !form.remarks?.trim()) e.remarks = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildPayload = (sourceSuffix = '') => {
    const fullName = [form.salutation, form.first_name, form.last_name].filter(Boolean).join(' ');
    return {
      name: fullName,
      email: form.email,
      phone: form.mobile ? `+91 ${form.mobile}` : undefined,
      destination: form.destination || form.destination_city || undefined,
      message: form.remarks || undefined,
      source: `enquiry-${category.toLowerCase()}${sourceSuffix}`,
      category,
      salutation: form.salutation,
      first_name: form.first_name,
      last_name: form.last_name,
      source_city: form.source_city,
      destination_city: form.destination_city,
      trip_type: form.trip_type,
      departure: form.departure,
      return_date: form.return_date,
      check_in: form.check_in,
      check_out: form.check_out,
      nationality: form.nationality,
      travel_date: form.travel_date,
      pax: form.pax,
      remarks: form.remarks,
    };
  };

  const buildWaMessage = () => {
    const lines = [`Hi *${company.name}*!`, `I'd like to enquire about *${category}*.`, ''];
    const push = (label, val) => { if (val) lines.push(`*${label}:* ${val}`); };
    push('Name', [form.salutation, form.first_name, form.last_name].filter(Boolean).join(' '));
    push('Mobile', form.mobile ? `+91 ${form.mobile}` : '');
    push('Email', form.email);
    push('Source City', form.source_city);
    push('Destination City', form.destination_city);
    push('Destination', form.destination);
    push('Trip Type', form.trip_type);
    push('Departure', form.departure);
    push('Return', form.return_date);
    push('Check In', form.check_in);
    push('Check Out', form.check_out);
    push('Travel Date', form.travel_date);
    push('Nationality', form.nationality);
    push('No. of Pax', form.pax);
    push('Remarks', form.remarks);
    return encodeURIComponent(lines.join('\n'));
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast({ title: 'Please fill the required fields', description: 'Fields marked with * are mandatory.' });
      return;
    }
    if (!robot) {
      toast({ title: 'Please confirm you are not a robot', description: "Tick the 'I'm not a robot' box to continue." });
      return;
    }
    setSubmitting(true);
    try {
      await createEnquiry(buildPayload());
      setSent(true);
      toast({ title: 'Enquiry sent!', description: 'Our travel specialists will reach out shortly.' });
      setTimeout(() => { setSent(false); onClose?.(); }, 1600);
    } catch (err) {
      toast({ title: 'Something went wrong', description: 'Please try again in a moment.' });
    } finally {
      setSubmitting(false);
    }
  };

  const onWhatsApp = async () => {
    if (!validate()) {
      toast({ title: 'Please fill the required fields', description: 'Fields marked with * are mandatory.' });
      return;
    }
    const url = `https://wa.me/${company.whatsapp}?text=${buildWaMessage()}`;
    window.open(url, '_blank', 'noopener');
    createEnquiry(buildPayload('-whatsapp')).catch(() => {});
    toast({ title: 'Opening WhatsApp…', description: 'Your enquiry details are ready to send.' });
    setTimeout(() => onClose?.(), 600);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto py-6 px-4" data-testid="enquiry-modal">
      <div className="absolute inset-0 bg-[#0B2545]/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white rounded-2xl shadow-[0_30px_80px_rgba(11,37,69,0.35)] my-auto animate-[fadeIn_.25s_ease]">
        <div className="flex items-center justify-between px-6 md:px-8 pt-6 pb-2">
          <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-[#0B2545]">Send Enquiry</h2>
          <button onClick={onClose} aria-label="Close" data-testid="enquiry-close-btn" className="text-[#0B2545] hover:text-[#EA5019] transition"><X size={26} /></button>
        </div>

        {/* Category tabs */}
        <div className="px-6 md:px-8 flex flex-wrap gap-2.5 pb-4 border-b border-slate-100">
          {CATEGORIES.map((c) => {
            const active = c.key === category;
            return (
              <button
                key={c.key}
                type="button"
                data-testid={`enquiry-tab-${c.key.toLowerCase()}`}
                onClick={() => { setCategory(c.key); setErrors({}); }}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium border transition-all ${
                  active
                    ? 'bg-[#EA5019]/10 border-[#EA5019] text-[#EA5019]'
                    : 'bg-white border-slate-300 text-[#0B2545] hover:border-[#EA5019]/60 hover:text-[#EA5019]'
                }`}
              >
                <c.icon size={15} /> {c.label}
              </button>
            );
          })}
        </div>

        <form onSubmit={onSubmit} className="px-6 md:px-8 py-5 space-y-4">
          {/* Name row */}
          <div className="grid grid-cols-1 sm:grid-cols-[110px_1fr_1fr] gap-3">
            <select
              data-testid="enquiry-salutation"
              value={form.salutation || ''}
              onChange={(e) => setField('salutation', e.target.value)}
              className={`${inputCls} text-slate-500`}
            >
              <option value="">Salutation</option>
              {SALUTATIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input data-testid="enquiry-first-name" placeholder="First Name *" value={form.first_name || ''} onChange={(e) => setField('first_name', e.target.value)} className={`${inputCls} ${errors.first_name ? 'border-red-400' : ''}`} />
            <input data-testid="enquiry-last-name" placeholder="Last Name *" value={form.last_name || ''} onChange={(e) => setField('last_name', e.target.value)} className={`${inputCls} ${errors.last_name ? 'border-red-400' : ''}`} />
          </div>

          {/* Mobile */}
          <div className={`flex items-stretch rounded-lg border ${errors.mobile ? 'border-red-400' : 'border-slate-300'} overflow-hidden focus-within:border-[#EA5019] focus-within:ring-2 focus-within:ring-[#EA5019]/15 transition`}>
            <span className="flex items-center gap-1.5 px-3 bg-slate-50 border-r border-slate-200 text-[#0B2545] text-sm font-medium whitespace-nowrap">
              <IndiaFlag /> +91
            </span>
            <input data-testid="enquiry-mobile" type="tel" placeholder="Mobile Number *" value={form.mobile || ''} onChange={(e) => setField('mobile', e.target.value.replace(/[^0-9]/g, ''))} maxLength={10} className="flex-1 px-4 py-3 text-[15px] text-[#0B2545] placeholder-slate-400 outline-none" />
          </div>

          {/* Email */}
          <input data-testid="enquiry-email" type="email" placeholder="E-Mail Address *" value={form.email || ''} onChange={(e) => setField('email', e.target.value)} className={`${inputCls} ${errors.email ? 'border-red-400' : ''}`} />

          {/* Dynamic fields grouped into rows of 2 (respecting `full`) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {fields.map((fl) => (
              <div key={fl.name} className={fl.full ? 'sm:col-span-2' : ''}>
                {fl.type === 'select' ? (
                  <select
                    data-testid={`enquiry-${fl.name.replace(/_/g, '-')}`}
                    value={form[fl.name] || ''}
                    onChange={(e) => setField(fl.name, e.target.value)}
                    className={`${inputCls} ${form[fl.name] ? '' : 'text-slate-500'} ${errors[fl.name] ? 'border-red-400' : ''}`}
                  >
                    <option value="">{fl.label}</option>
                    {fl.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : fl.type === 'date' ? (
                  <div className="relative">
                    {!form[fl.name] && (
                      <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-[15px]">
                        {fl.label}{fl.req ? ' *' : ''}
                      </span>
                    )}
                    <input
                      data-testid={`enquiry-${fl.name.replace(/_/g, '-')}`}
                      type="date"
                      value={form[fl.name] || ''}
                      onChange={(e) => setField(fl.name, e.target.value)}
                      className={`${inputCls} ${errors[fl.name] ? 'border-red-400' : ''} ${form[fl.name] ? 'text-[#0B2545]' : 'text-transparent'}`}
                    />
                  </div>
                ) : (
                  <input
                    data-testid={`enquiry-${fl.name.replace(/_/g, '-')}`}
                    type={fl.type === 'number' ? 'number' : 'text'}
                    min={fl.type === 'number' ? 1 : undefined}
                    placeholder={`${fl.label}${fl.req ? ' *' : ''}`}
                    value={form[fl.name] || ''}
                    onChange={(e) => setField(fl.name, e.target.value)}
                    className={`${inputCls} ${errors[fl.name] ? 'border-red-400' : ''}`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Remarks */}
          <textarea data-testid="enquiry-remarks" rows={3} placeholder={category === 'Miscellaneous' ? 'Remarks *' : 'Remarks'} value={form.remarks || ''} onChange={(e) => setField('remarks', e.target.value)} className={`${inputCls} resize-none ${errors.remarks ? 'border-red-400' : ''}`} />

          {/* Visual robot check */}
          <label data-testid="enquiry-robot" className="flex items-center justify-between gap-3 rounded-lg border border-slate-300 bg-slate-50 px-4 py-4 cursor-pointer select-none">
            <span className="flex items-center gap-3">
              <input type="checkbox" checked={robot} onChange={(e) => setRobot(e.target.checked)} className="h-6 w-6 rounded border-slate-400 accent-[#EA5019]" />
              <span className="text-[#0B2545] text-[15px]">I'm not a robot</span>
            </span>
            <span className="text-[10px] leading-tight text-slate-400 text-right">
              reCAPTCHA<br />Privacy · Terms
            </span>
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="submit"
              data-testid="enquiry-submit-btn"
              disabled={submitting}
              className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold text-white transition-all ${sent ? 'bg-[#22a06b]' : 'bg-[#EA5019] hover:bg-[#cf4413]'} ${submitting ? 'opacity-70 cursor-not-allowed' : ''} shadow-[0_10px_26px_rgba(234,80,25,0.35)]`}
            >
              {sent ? (<><CheckCircle2 size={20} /> Enquiry Sent</>) : submitting ? 'Sending…' : (<><Send size={18} /> Send Enquiry</>)}
            </button>
            <button
              type="button"
              onClick={onWhatsApp}
              data-testid="enquiry-whatsapp-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold text-white bg-[#25D366] hover:brightness-95 transition-all shadow-[0_10px_26px_rgba(37,211,102,0.35)]"
            >
              <MessageCircle size={19} /> Send via WhatsApp
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
